import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute, { TenantRoute, LandlordRoute } from './components/PrivateRoute';
import { apiGet } from './api';
import { buildPortalUrl } from './utils/portalLink';
import { websiteRedirectFor } from './utils/authRedirect';
import ScrollToTop from './components/scroll-to-top';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import MaintenanceScreen from './components/MaintenanceScreen';
import KycAlertBanner from './components/KycAlertBanner';
import KycAlertModal from './components/KycAlertModal';
import PushManager from './components/PushManager';

// Lazy load route components for code splitting
const Video = lazy(() => import('./pages/index/video'));
const Map = lazy(() => import('./pages/index/map'));
const ListLayoutOne = lazy(() => import('./pages/property/list-layout-one'));
const Grid = lazy(() => import('./pages/property/grid'));
const HalfMapTwo = lazy(() => import('./pages/property/half-map-2'));
// Canonical property detail page. Reached at /property/:id/:slug (slug is for
// SEO / social sharing only — the page always fetches by ID).
const PropertyDetail = lazy(() => import('./pages/property/single-property-1'));
const Agents = lazy(() => import('./pages/agents/agents'));
const AgentPage = lazy(() => import('./pages/agents/agent-page'));
const Agencies = lazy(() => import('./pages/agents/agencies'));
const AgencyPage = lazy(() => import('./pages/agents/agency-page'));
const Dashboard = lazy(() => import('./pages/admin/dashboard'));
const Payment = lazy(() => import('./pages/admin/payment'));
const MyProfile = lazy(() => import('./pages/admin/my-profile'));
const MyProperty = lazy(() => import('./pages/admin/my-property'));
const BookmarkList = lazy(() => import('./pages/admin/bookmark-list'));
const ChangePassword = lazy(() => import('./pages/admin/change-password'));
const CompareProperty = lazy(() => import('./pages/property/compare-property'));
// The website has no property-submission form — listing is done in the portal
// wizard (/properties/new). Any "Submit Property" entry point redirects there;
// the portal bootstraps auth via the HttpOnly cookie / login on arrival.
function SubmitPropertyRedirect() {
  useEffect(() => {
    window.location.replace(buildPortalUrl('/properties/new'));
  }, []);
  return (
    <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
      Redirecting you to the property submission portal…
    </div>
  );
}
const Blog = lazy(() => import('./pages/blog/blog'));
const BlogDetail = lazy(() => import('./pages/blog/blog-detail'));
const Component = lazy(() => import('./pages/component'));
const Pricing = lazy(() => import('./pages/pricing'));
const Error = lazy(() => import('./pages/error'));
const Contact = lazy(() => import('./pages/contact'));
const AboutUs = lazy(() => import('./pages/about-us'));
const Checkout = lazy(() => import('./pages/admin/checkout'));
const CreateAccount = lazy(() => import('./pages/admin/create-account'));
const ConfirmIdentity = lazy(() => import('./pages/admin/confirm-identity'));
const ReportVerify = lazy(() => import('./pages/reports/verify'));
const Login = lazy(() => import('./pages/admin/login'));
const Faq = lazy(() => import('./pages/faq'));
const HowItWorks = lazy(() => import('./pages/how-it-works'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfUse = lazy(() => import('./pages/terms-of-use'));
const TenantLayout = lazy(() => import('./components/TenantLayout'));
const TenantDashboard = lazy(() => import('./pages/tenant/dashboard'));
const TenantEnquiries = lazy(() => import('./pages/tenant/enquiries'));
const TenantNotifications = lazy(() => import('./pages/tenant/notifications'));
const TenantProfile = lazy(() => import('./pages/tenant/profile'));
const TenantChangePassword = lazy(() => import('./pages/tenant/change-password'));
const TenantConversations = lazy(() => import('./pages/tenant/conversations'));
const TenantConversationDetail = lazy(() => import('./pages/tenant/conversation-detail'));
const AddReview = lazy(() => import('./pages/add-review'));
const PropertyRequests = lazy(() => import('./pages/admin/property-requests'));
const SearchResults = lazy(() => import('./pages/search-results'));
const AgencyOnboarding = lazy(() => import('./pages/admin/agency-onboarding'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--srs-bg, #F7F7FB)'
  }}>
    <div className="loading-spinner" style={{
      width: '50px',
      height: '50px',
      border: '4px solid rgba(194, 136, 64, 0.1)',
      borderTopColor: 'var(--srs-gold, #C28840)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }}></div>
  </div>
);

function App() {
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState<{ message?: string } | null>(null);

  // Show a full-page maintenance notice when api.ts reports maintenance mode
  // (503 + { maintenance: true }). Event-driven so there is no extra request on
  // a healthy load — the screen appears the moment any API call hits the gate.
  useEffect(() => {
    const onMaintenance = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail;
      setMaintenance({ message: detail?.message });
    };
    window.addEventListener('srs:maintenance', onMaintenance);
    return () => window.removeEventListener('srs:maintenance', onMaintenance);
  }, []);

  // Redirect to /login when api.ts detects an expired JWT (401 with a stored token).
  // Using a custom event keeps api.ts free of React Router imports while still
  // respecting the BrowserRouter basename configured in index.tsx.
  useEffect(() => {
    const handleSessionExpired = () => navigate('/login');
    window.addEventListener('srs:session-expired', handleSessionExpired);
    return () => window.removeEventListener('srs:session-expired', handleSessionExpired);
  }, [navigate]);

  // Cross-tab sync: if the user logs out (or in) in another tab, mirror it here.
  // The Web Storage "storage" event fires in every tab EXCEPT the one that made the change.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'srs_user' && !e.newValue) {
        // Session cleared in another tab — redirect this tab to login
        navigate('/login');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  // Automatic application routing + session recovery (runs once per full load:
  // refresh, reopened browser, bookmarked URL, manual URL entry).
  //
  // Validate the HttpOnly-cookie session against the backend and let the backend
  // (auth_routing) decide whether this user belongs on the Website. Portal users
  // who land on the Website are sent straight to the Portal — they never see a
  // Website dashboard. Anonymous visitors get a 401 (no prior session) and stay
  // on the public site. No loop: the bounce target is the user's home app, which
  // is always one of their allowed apps.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet<{ data?: unknown }>('/auth/me');
        if (cancelled) return;
        const me = (res && typeof res === 'object' && 'data' in res)
          ? (res as { data?: any }).data
          : (res as any);
        if (!me) return;
        // Session recovery — cache the freshly-validated user.
        localStorage.setItem('srs_user', JSON.stringify(me));
        window.dispatchEvent(new Event('userUpdated'));
        const portalPath = websiteRedirectFor(me);
        if (portalPath) {
          window.location.href = buildPortalUrl(portalPath);
        }
      } catch {
        // Anonymous visitor or expired/invalid cookie — nothing to enforce here.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (maintenance) {
    return <MaintenanceScreen message={maintenance.message} />;
  }

  return (
    <ErrorBoundary>
      <SiteSettingsProvider>
      <Suspense fallback={<LoadingFallback />}>
        {/* Global KYC nudge — shown on all pages for unverified tenants */}
        <KycAlertBanner />
        <KycAlertModal />
        {/* Foreground push toasts (when the user has opted into notifications) */}
        <PushManager />

        <Routes>
          {/* ── Public routes ─────────────────────────────────────────────── */}
          <Route path='/' element={<Video />} />
          {/* Demo home templates → canonical home */}
          <Route path='/home-2' element={<Navigate to="/" replace />} />
          <Route path='/home-3' element={<Navigate to="/" replace />} />
          <Route path='/home-4' element={<Navigate to="/" replace />} />
          <Route path='/home-5' element={<Navigate to="/" replace />} />
          <Route path='/home-6' element={<Navigate to="/" replace />} />
          <Route path='/home-7' element={<Navigate to="/" replace />} />
          <Route path='/home-8' element={<Navigate to="/" replace />} />
          <Route path='/home-9' element={<Navigate to="/" replace />} />
          <Route path='/home-10' element={<Navigate to="/" replace />} />
          <Route path='/home-11' element={<Navigate to="/" replace />} />
          <Route path='/video' element={<Navigate to="/" replace />} />
          <Route path='/map' element={<Map />} />

          {/* Canonical listing paths; legacy layout demos redirect */}
          <Route path='/list-layout-one' element={<ListLayoutOne />} />
          <Route path='/list-layout-two' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/list-layout-three' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/list-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/list-layout-full' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/grid-layout-with-sidebar' element={<Navigate to="/grid" replace />} />
          <Route path='/classical-layout-with-sidebar' element={<Navigate to="/grid" replace />} />
          <Route path='/grid-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/grid' element={<Grid />} />
          <Route path='/classical-property' element={<Navigate to="/grid" replace />} />
          <Route path='/classical-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/half-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/half-map-2' element={<HalfMapTwo />} />
          {/* Canonical property detail — SEO-friendly slug URL. Slug is optional
              (page redirects to the canonical slug once the property loads). */}
          <Route path='/property/:id' element={<PropertyDetail />} />
          <Route path='/property/:id/:slug' element={<PropertyDetail />} />
          <Route path='/agents' element={<Agents />} />
          <Route path='/agent-page' element={<AgentPage />} />
          <Route path='/agent-page/:id' element={<AgentPage />} />
          <Route path='/agencies' element={<Agencies />} />
          <Route path='/agency-page' element={<AgencyPage />} />
          <Route path='/agency-page/:id' element={<AgencyPage />} />
          <Route path='/compare-property' element={<CompareProperty />} />
          <Route path='/blog' element={<Blog />} />
          <Route path='/blog-detail' element={<BlogDetail />} />
          <Route path='/blog-detail/:id' element={<BlogDetail />} />
          <Route path='/component' element={<Component />} />
          <Route path='/pricing' element={<Pricing />} />
          <Route path='/404' element={<Error />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/about-us' element={<AboutUs />} />
          <Route path='/how-it-works' element={<HowItWorks />} />
          <Route path='/create-account' element={<CreateAccount />} />
          <Route path='/confirm-identity' element={<ConfirmIdentity />} />
          <Route path='/reports/verify/:qrToken' element={<ReportVerify />} />
          <Route path='/login' element={<Login />} />
          <Route path='/faq' element={<Faq />} />
          <Route path='/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/terms-of-use' element={<TermsOfUse />} />
          <Route path='/terms-and-conditions' element={<Navigate to="/terms-of-use" replace />} />
          <Route path='/place-search' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/search-results' element={<SearchResults />} />

          {/* ── Private routes — redirect to /login if no session ────────── */}
          <Route element={<PrivateRoute />}>
            {/* Any authenticated user */}
            <Route path='/dashboard' element={<Dashboard />} />
            <Route path='/payment' element={<Payment />} />
            <Route path='/my-profile' element={<MyProfile />} />
            <Route path='/bookmark-list' element={<BookmarkList />} />
            <Route path='/change-password' element={<ChangePassword />} />
            <Route path='/checkout' element={<Checkout />} />
            <Route path='/add-review' element={<AddReview />} />

            {/* Landlord / Agent / Agency only */}
            <Route element={<LandlordRoute />}>
              {/* Agency onboarding — requires a non-tenant role */}
              <Route path='/agency-onboarding' element={<AgencyOnboarding />} />
              <Route path='/my-property' element={<MyProperty />} />
              <Route path='/submit-property' element={<SubmitPropertyRedirect />} />
              <Route path='/submit-property-dashboard' element={<SubmitPropertyRedirect />} />
              <Route path='/property-requests' element={<PropertyRequests />} />
            </Route>

            {/* Tenant only */}
            <Route element={<TenantRoute />}>
              {/* Tenant Dashboard — standalone full-width page */}
              <Route path='/tenant/dashboard' element={<TenantDashboard />} />

              {/* Other tenant pages — nested under TenantLayout with sidebar */}
              <Route path='/tenant' element={<TenantLayout />}>
                {/* /tenant with no sub-path → go to dashboard */}
                <Route index element={<Navigate to="/tenant/dashboard" replace />} />
                <Route path='enquiries' element={<TenantEnquiries />} />
                <Route path='enquiries/:id' element={<TenantEnquiries />} />
                <Route path='notifications' element={<TenantNotifications />} />
                <Route path='profile' element={<TenantProfile />} />
                <Route path='change-password' element={<TenantChangePassword />} />
                <Route path='conversations' element={<TenantConversations />} />
                <Route path='conversations/:id' element={<TenantConversationDetail />} />
              </Route>
            </Route>
          </Route>
        </Routes>
        <ScrollToTop />
      </Suspense>
      </SiteSettingsProvider>
    </ErrorBoundary>
  );
}

export default App;
